import React, { useMemo } from 'react';
import { PlusCircleIcon, TrashIcon } from '@iconicicons/react';
import { PopupSegmentConstraints } from '../map/popups/PopupSegmentConstraints';
import { EdgeFlowTable } from './EdgeFlow';
import { Edge, useDeleteEdgeMutation } from '../services/edgeApi';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { selectIsMapLoading } from '../map/slice/mapSelectors';
import toast from 'react-hot-toast';
import { EdgeCapacitiesTable } from './EdgeCapacitiesTable';
import { Operation } from '@/types';
import { UncontrolledTooltip } from 'reactstrap';
import { IconButton } from '../helpers/IconButton';

export type EdgePopupProps = {
  edge: Edge | null;
  operations: Operation[];
  showScenarioInfo?: boolean;
  closePopup?: () => void;
  onAddNode?: () => void;
};

export const EdgePopup: React.FC<EdgePopupProps> = ({
  edge,
  operations,
  showScenarioInfo,
  closePopup,
  onAddNode,
}) => {
  const location = useLocation();
  const [deleteEdgeTrigger, deleteEdgeRequestInfo] = useDeleteEdgeMutation();

  const isMapLoading = useSelector(selectIsMapLoading);

  const isMapBuilderPage = useMemo(() => {
    return location.pathname.includes('/map-builder');
  }, [location.pathname]);

  if (!edge) return null;

  const deleteEdge = async () => {
    if (
      confirm(
        `Are you sure you want to delete edge ${edge.tsp_name} #${edge.id}?`,
      )
    ) {
      try {
        await deleteEdgeTrigger(edge.id).unwrap();
        closePopup?.();
        toast.success('Edge deleted successfully');
      } catch (error: any) {
        console.error('Failed to delete edge:', error);
        toast.error(error?.data?.message ?? 'Failed to delete edge');
      }
    }
  };

  return (
    <>
      <small>
        #{edge.id}
        <span className="float-end">
          {isMapBuilderPage && isMapLoading === false && (
            <>
              <IconButton
                id="delete-edge-button"
                role="button"
                disabled={deleteEdgeRequestInfo.isLoading}
                disabledStyle={{ cursor: 'wait' }}
                onClick={deleteEdge}
                icon={TrashIcon}
              />
              <UncontrolledTooltip target="delete-edge-button" placement="top">
                Delete edge
              </UncontrolledTooltip>
              <PlusCircleIcon
                id="split-edge-button"
                role="button"
                className="ms-2"
                onClick={onAddNode}
              />
              <UncontrolledTooltip target="split-edge-button" placement="top">
                Split edge with a new node
              </UncontrolledTooltip>
            </>
          )}
        </span>
      </small>
      <p className="my-0 fw-bold">{edge.tsp_name}</p>
      <p className="my-0">
        {edge.zone ? `Zone: ${edge.zone}` : ''}{' '}
        {edge.seg_nbr ? ` | Seg: ${edge.seg_nbr}` : null}
      </p>
      <EdgeFlowTable
        operations={operations}
        className="table table-bordered table-sm"
      />
      {showScenarioInfo && (
        <PopupSegmentConstraints
          edgeId={edge.id?.toString()}
          tspId={edge.tsp_id?.toString()}
        />
      )}
      {isMapBuilderPage && <EdgeCapacitiesTable edge={edge} />}
    </>
  );
};
